// SceneryVariants — Set13 §3.2: derive STRUCTURAL variants from a resolved kind's param swings via
// Latin-hypercube (stratified) sampling.
//
// WHY LHS, not independent hashing (§3.2 a): with only ~6 variants, independent per-param hashes
// routinely cluster mid-range and yield 6 near-identical trees — the exact failure this feature
// exists to avoid. Instead: variant i draws from the i-th of `variantCount` equal strata of each
// swung param's span, and the stratum→variant permutation is SHUFFLED INDEPENDENTLY per param.
// That second half is load-bearing: a SHARED permutation produces a 1-D family (variant 0 =
// small+sparse+pale … variant 5 = big+dense+dark) rather than 6 genuinely distinct objects.
// Fully deterministic from `(seed, paramKey)` via pcgHash.
//
// ⚠ RULES:
//  (b) Clamp at SAMPLE time, never at author time: `value ± swing` may fall outside the schema
//      `[min, max]`. The authored numbers stay untouched — clamping the STORED value/swing is
//      destructive (scrub a slider out of range and back, and the swing is silently gone).
//  (c) INTERSECT, then stratify — NOT stratify then clamp. The sampling span is
//      `[value-swing, value+swing] ∩ [schema.min, schema.max]`, and the strata are cut over THAT
//      intersection. Stratifying the unclamped span and clamping afterwards collapses every
//      out-of-range stratum onto the boundary → DUPLICATE variants (the "my 6 variants render as
//      4" silent variety loss).
//  (d) A param with a resolved swing of 0 (or absent swingDefault) is NOT structural → the SAME
//      value in every variant (the fixed case).
import {SceneryKindDef, SceneryVariant, asNumber, type ParamValue, type ResolvedSceneryParam} from "./@SceneryTypes.js";
import {hashString32, pcgHash} from "./SceneryHash.js";
import {resolveSceneryKindFromChain, type ResolvedSceneryKind} from "./SceneryResolverCore.js";

/** Derive the kind's structural variants. Deterministic from `(seed, variantCount, paramKeys)`. */
export function deriveVariants(def: SceneryKindDef | ResolvedSceneryKind): SceneryVariant[] {
	// Accept either a raw def (resolve its chain-free self) or an already-resolved kind. To keep
	// this function pure/DB-free and avoid a circular import, we resolve a chain-free def here if
	// needed by importing lazily — but a raw def has no base chain, so resolving "self" is fine.
	let resolved: ResolvedSceneryKind;
	if (isResolvedKind(def)) {
		resolved = def;
	} else {
		// Chain-free (a root def used directly): construct a resolved kind inline.
		resolved = resolveSceneryKindFromChain([def as SceneryKindDef]);
	}

	const variantCount = Math.max(1, resolved.variantCount);
	const swungParams = resolved.params.filter(p=>resolvedSwing(p) > 0);

	// Per-param strata permutation, decorrelated by (seed, paramKey). The stratum→variant mapping
	// for param p is a SHUFFLE of [0, variantCount), derived from a per-param hash stream.
	const permFor = (p: ResolvedSceneryParam): number[]=>{
		const perm = Array.from({length: variantCount}, (_, i)=>i);
		const stream = hashString32(`${resolved.seed}:${p.key}`);
		// Fisher-Yates with a deterministic per-index hash stream.
		for (let i = variantCount - 1; i > 0; i--) {
			const j = pcgHash((stream + Math.imul(i, 0x9E3779B1)) >>> 0) % (i + 1);
			const tmp = perm[i]; perm[i] = perm[j]; perm[j] = tmp;
		}
		return perm;
	};

	const variants: SceneryVariant[] = [];
	for (let i = 0; i < variantCount; i++) {
		const params: {[key: string]: ParamValue} = {};
		for (const p of resolved.params) {
			const swing = resolvedSwing(p);
					// Set14 P2 — non-swingable KINDS (enum/bool/vec3) are NEVER swung, even if a swing is
					// somehow set (the editor refuses to set one, but a hand-edited kind could carry one).
					// They are structural constants per variant — their value is the resolved value verbatim.
					// `int` is swingable but its value is ROUNDED via swingableValue below (the no-swing
					// fixed case still passes through the int-rounding).
			if (swing <= 0 || isNonSwingableKind(p)) {
				params[p.key] = swingableValue(p);
				continue;
			}
			// (b/c) INTERSECT the span with the schema range, then stratify over the intersection.
			const base = asNumber(p.value) ?? 0;
			const min = typeof p.schema.min == "number" ? p.schema.min : 0;
			const max = typeof p.schema.max == "number" ? p.schema.max : 1;
			const lo = Math.max(base - swing, min);
			const hi = Math.min(base + swing, max);
			const width = hi - lo;
			// The stratum for this variant, over the INTERSECTION. `perm[i]` maps variant i → a
			// stratum index (0..variantCount-1), and the sample is the CENTRE of that stratum.
			const stratumIdx = permFor(p)[i];
			const t = (stratumIdx + 0.5) / variantCount;   // 0..1 within the intersected span
			let sampled: number;
			if (p.curve == "log" && base > 0) {
				// Geometric: value ×/÷ around base by factor `f = 1+swing/base` to the ±t power.
				// The stratum fraction t maps linearly in LOG space over [log(lo), log(hi)].
				const logLo = Math.log(Math.max(lo, 1e-6));
				const logHi = Math.log(Math.max(hi, 1e-6));
				sampled = Math.exp(logLo + t * (logHi - logLo));
			} else {
				sampled = lo + t * width;
			}
			params[p.key] = swingableValue({...p, value: sampled});
		}
		variants.push({index: i, params, weight: variantWeight(i, resolved)});
	}
	return variants;
}

/** Kinds that are NEVER structural/swung (§4.3 a / §4.13).  `int` IS swingable (a numeric stratum,
 *  rounded after sampling); `enum`/`bool`/`vec3` are discrete/non-ordinal carriers with no
 *  meaningful swing, so they are identical in every variant. */
function isNonSwingableKind(p: ResolvedSceneryParam): boolean {
	const k = p.schema.kind;
	return k == "enum" || k == "bool" || k == "vec3";
}

/** The per-variant VALUE for a param, post-processing its sampled/resolved carrier:
 *  - int: round the sampled numeric stratum to an integer (a variant of an int param is an int).
 *  - scalar/color/ramp/enum/bool/vec3: passthrough (swung only reaches here for numeric kinds).
 */
function swingableValue(p: ResolvedSceneryParam): ParamValue {
	if (p.schema.kind == "int") {
		const n = asNumber(p.value);
		return n == null ? (typeof p.schema.default == "number" ? Math.round(p.schema.default) : 0) : Math.round(n);
	}
	return p.value;
}

/** The resolved swing of a param (schema default, possibly patched through the chain). */
function resolvedSwing(p: ResolvedSceneryParam): number {
	return typeof p.swing == "number" && Number.isFinite(p.swing) ? p.swing : 0;
}

/** The (unnormalised) selection weight of variant i — from `variantWeights` when present and
 *  correctly sized, else uniform 1. Callers normalise across the kind's variants. */
function variantWeight(i: number, resolved: ResolvedSceneryKind): number {
	const w = resolved.variantWeights;
	if (w != null && w.length == resolved.variantCount) return w[i];
	return 1;
}

function isResolvedKind(d: SceneryKindDef | ResolvedSceneryKind): d is ResolvedSceneryKind {
	return (d as ResolvedSceneryKind).chainIDs != null;
}
